import React, { useState } from "react";
import { Modal, Button, Upload, Input, Alert, Progress, Space, Card, DatePicker } from "antd";
import { UploadOutlined, FileTextOutlined, FilePdfOutlined, FileImageOutlined, DeleteOutlined } from "@ant-design/icons";
import { useNotifications } from "../../../context/NotificationContext";
import { getSessionUser } from "../../../utils/auth/sessionUser";
import dayjs, { Dayjs } from "dayjs";

// Types
import { User } from "../../../types/admin/UserTypes";
import { patientUploadTestResults } from "../../../api/healthcare/Testing/TestApi";
import { downscaleImage } from "../../../utils/downscaleImage";

const { TextArea } = Input;

interface PatientBloodTestUploadModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: () => void;
}

interface UploadedFile {
	file: File;
	name: string;
	size: number;
	type: string;
	preview?: string;
}

const PatientBloodTestUploadModal: React.FC<PatientBloodTestUploadModalProps> = ({ isOpen, onClose, onSuccess }) => {
	const user: User = getSessionUser();
	const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
	const [notes, setNotes] = useState<string>("");
	const [testDate, setTestDate] = useState<Dayjs | null>(null);
	const [facilityName, setFacilityName] = useState<string>("");
	const [resultStatus, setResultStatus] = useState<string>("normal");
	const [uploading, setUploading] = useState<boolean>(false);
	const [uploadProgress, setUploadProgress] = useState<number>(0);
	const [performAnalysis, setPerformAnalysis] = useState<boolean>(true);

	const { showError, showSuccess } = useNotifications();

	const resultStatusOptions = [
		{ label: "Normal", value: "normal" },
		{ label: "Abnormal", value: "abnormal" },
		{ label: "Critical", value: "critical" },
		{ label: "Pending Review", value: "pending_review" },
	];

	const allowedFileTypes = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];

	// Matches BloodTestAnalysisService::MAX_DOCUMENT_BYTES — a larger file fails after the queue round-trip.
	const maxFileSize = 5 * 1024 * 1024;

	const isPdfPasswordProtected = (file: File): Promise<boolean> => {
		return new Promise((resolve) => {
			const reader = new FileReader();
			reader.onload = (e) => {
				const buffer = e.target?.result as ArrayBuffer;
				// All encrypted PDFs contain an /Encrypt entry in their xref trailer
				const content = new TextDecoder("latin1").decode(new Uint8Array(buffer));
				resolve(content.includes("/Encrypt"));
			};
			reader.onerror = () => resolve(false);
			reader.readAsArrayBuffer(file);
		});
	};

	const handleFileSelect = async (file: File) => {
		// Validate file type
		if (!allowedFileTypes.includes(file.type)) {
			showError(`Invalid file type: ${file.name}. Please upload PDF, JPG or PNG files only.`);
			return false;
		}

		// Downscale before the size check so the limit applies to the file actually sent.
		const processed = await downscaleImage(file);

		// Validate file size
		if (processed.size > maxFileSize) {
			showError(`File too large: ${file.name}. Maximum size is 5MB — please compress the file or split it into smaller documents.`);
			return false;
		}

		// Reject password-protected PDFs before upload — downscaling passes a PDF through untouched.
		if (processed.type === "application/pdf") {
			const isProtected = await isPdfPasswordProtected(processed);
			if (isProtected) {
				showError(`"${processed.name}" is password-protected and cannot be uploaded. Please remove the password protection and try again.`);
				return false;
			}
		}

		// Create preview for images
		let preview: string | undefined;
		if (processed.type.startsWith("image/")) {
			preview = URL.createObjectURL(processed);
		}

		setUploadedFiles((prev) => [
			...prev,
			{
				file: processed,
				name: processed.name,
				size: processed.size,
				type: processed.type,
				preview,
			},
		]);

		return false; // Prevent automatic upload
	};

	const handleFileRemove = (index: number) => {
		setUploadedFiles((prev) => {
			const newFiles = [...prev];
			if (newFiles[index].preview) {
				URL.revokeObjectURL(newFiles[index].preview!);
			}
			newFiles.splice(index, 1);
			return newFiles;
		});
	};

	const handleSave = async () => {
		if (uploadedFiles.length === 0) {
			showError("Please upload at least one result document.");
			return;
		}

		setUploading(true);
		setUploadProgress(0);

		try {
			const formData = new FormData();

			// Auto-fill required backend fields for test order
			formData.append("patient_id", user.id.toString());
				formData.append("test_type", "mygene_panels");
			formData.append("test_category", "blood_tests");
			formData.append("test_name", "Blood Test - Patient Upload");
			formData.append("priority", "routine");
			// Uploading a document does NOT mean results are ready — the analysis runs
			// asynchronously afterwards, so the order starts "In Progress".
			formData.append("status", "in_progress");
			
			// Optional test order fields
			if (testDate) {
				formData.append("test_date", testDate.format("YYYY-MM-DD"));
			}
			if (facilityName) {
				formData.append("facility", facilityName);
			}
			formData.append("reason", "Patient self-upload");

			// Test result fields - prefixed with 'result.'
			formData.append("result.result_notes", notes);
			formData.append("result.is_abnormal", resultStatus === "abnormal" || resultStatus === "critical" ? "1" : "0");
			formData.append("result.requires_followup", resultStatus === "critical" || resultStatus === "pending_review" ? "1" : "0");
			formData.append(
				"result.results",
				JSON.stringify(
					uploadedFiles.map((file) => ({
						name: file.name,
						size: file.size,
						type: file.type,
					}))
				)
			);
			formData.append("result.perform_analysis", performAnalysis ? "1" : "0");
			formData.append("result.uploaded_by", user.id.toString());
			formData.append("result.status", "in_progress");

			// Add uploaded files - prefixed with 'result.'
			uploadedFiles.forEach((uploadedFile) => {
				formData.append(`result.files[]`, uploadedFile.file);
			});

			const progressInterval = setInterval(() => {
				setUploadProgress((prev) => {
					if (prev >= 90) {
						clearInterval(progressInterval);
						return prev;
					}
					return prev + 10;
				});
			}, 200);

			const response = await patientUploadTestResults(user.id, "blood_tests", formData);

			clearInterval(progressInterval);
			setUploadProgress(100);

			// The upload response cannot know how many collections a report holds — the
			// analysis job finds that out minutes later — so report only what was queued.
			const uploadedCount = response?.tests?.length || uploadedFiles.length;

			// 86d3vhb3j S7 — no notification promise here. Only the practitioner is
			// notified when a blood test analysis finishes; the patient never is.
			showSuccess(
				`Successfully uploaded ${uploadedCount} blood test result document(s)${performAnalysis ? ". Your results are being analysed and will appear here once they are ready — a report covering several collection dates produces one result per date." : ""}`
			);
			handleClose();
			
			if (onSuccess) {
				onSuccess();
			}
		} catch (error) {
			console.error("Upload error:", error);
			showError("Failed to upload blood test results. Please try again.");
		} finally {
			setUploading(false);
		}
	};

	const handleClose = () => {
		uploadedFiles.forEach((file) => {
			if (file.preview) {
				URL.revokeObjectURL(file.preview);
			}
		});

		setUploadedFiles([]);
		setNotes("");
		setTestDate(null);
		setFacilityName("");
		setResultStatus("normal");
		setUploading(false);
		setUploadProgress(0);
		setPerformAnalysis(true);
		onClose();
	};

	const formatFileSize = (bytes: number): string => {
		if (bytes === 0) return "0 Bytes";
		const k = 1024;
		const sizes = ["Bytes", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
	};

	const getFileIcon = (fileType: string) => {
		if (fileType.includes("pdf")) return <FilePdfOutlined style={{ fontSize: 24, color: "#f5222d" }} />;
		if (fileType.includes("image")) return <FileImageOutlined style={{ fontSize: 24, color: "#52c41a" }} />;
		return <FileTextOutlined style={{ fontSize: 24 }} />;
	};

	return (
		<Modal
			title={
				<Space>
					<UploadOutlined />
					<span>Upload Blood Test Results</span>
				</Space>
			}
			open={isOpen}
			onCancel={handleClose}
			footer={[
				<Button key="cancel" onClick={handleClose} disabled={uploading}>
					Cancel
				</Button>,
				<Button key="submit" type="primary" onClick={handleSave} loading={uploading} disabled={uploading}>
					{uploading ? "Uploading..." : "Upload Results"}
				</Button>,
			]}
			width={800}
			style={{ maxWidth: "90vw" }}
			closable={!uploading}
		>
			<Space direction="vertical" style={{ width: "100%" }} size="large">

				{uploading && (
					<div>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
							<span style={{ fontWeight: 500 }}>Uploading files...</span>
							<span style={{ fontSize: 14, opacity: 0.65 }}>{uploadProgress}%</span>
						</div>
						<Progress percent={uploadProgress} status="active" />
					</div>
				)}

				<div>
					<label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
						Upload Result Documents <span style={{ color: "#ff4d4f" }}>*</span>
					</label>

					<Upload.Dragger accept=".pdf,.jpg,.jpeg,.png" beforeUpload={handleFileSelect} showUploadList={false} disabled={uploading}>
						<p className="ant-upload-drag-icon">
							<UploadOutlined />
						</p>
						<p className="ant-upload-text">Click or drag files to this area to upload</p>
						<p className="ant-upload-hint">Support for PDF, JPG, PNG. Maximum 5MB per file.</p>
					</Upload.Dragger>

					<Alert message="Accepted formats: PDF, JPG, PNG. Maximum size: 5MB per file." type="info" showIcon style={{ marginTop: 12 }} />
					<Alert message="Please note that password-protected files cannot be uploaded." type="info" showIcon style={{ marginTop: 12 }} />

					{uploadedFiles.length > 0 && (
						<Card size="small" title={`Selected Files (${uploadedFiles.length})`} style={{ marginTop: 16 }}>
							<Space direction="vertical" style={{ width: "100%" }} size="small">
								{uploadedFiles.map((file, index) => (
									<div
										key={index}
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											padding: 8,
											borderBottom: index < uploadedFiles.length - 1 ? "1px solid rgba(0,0,0,0.06)" : "none",
										}}
									>
										<Space>
											{getFileIcon(file.type)}
											<div>
												<div style={{ fontWeight: 500 }}>{file.name}</div>
												<div style={{ fontSize: 12, opacity: 0.65 }}>{formatFileSize(file.size)}</div>
											</div>
										</Space>
										<Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleFileRemove(index)} disabled={uploading} size="small" />
									</div>
								))}
							</Space>
						</Card>
					)}
				</div>

				<div>
					<label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Test Date (Optional)</label>
					<DatePicker 
						value={testDate} 
						onChange={setTestDate} 
						placeholder="When was the test performed?" 
						style={{ width: "100%" }} 
						disabled={uploading}
						disabledDate={(current) => current && current > dayjs().endOf('day')}
					/>
				</div>

				<div>
					<label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Facility/Lab Name (Optional)</label>
					<Input 
						value={facilityName} 
						onChange={(e) => setFacilityName(e.target.value)} 
						placeholder="Enter the name of the lab or facility" 
						disabled={uploading}
					/>
				</div>

				<div>
					<label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Additional Notes (Optional)</label>
					<TextArea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Enter any additional notes about the test results..." rows={4} disabled={uploading} />
				</div>
			</Space>
		</Modal>
	);
};

export default PatientBloodTestUploadModal;
